import { useMutation, useQueryClient } from '@tanstack/react-query';

import { KubesoloOfflineBundle } from '@api/types.gen';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';

import { queryKeys } from './queryKeys';

interface Upload {
  file: File;
  /** Called with the percent of the file sent so far */
  onProgress(percent: number): void;
}

export function useUploadOfflineBundleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadOfflineBundle,
    onSuccess: () => queryClient.invalidateQueries(queryKeys.offlineBundles()),
    ...withError('Unable to upload the offline bundle'),
  });
}

/**
 * Sends the tarball as the raw request body, which the generated client can't: it validates
 * the body against the spec, which has no binary type for it.
 */
async function uploadOfflineBundle({ file, onProgress }: Upload) {
  try {
    const { data } = await axios.put<KubesoloOfflineBundle>(
      `/kubesolo/offline-bundles/${encodeURIComponent(file.name)}`,
      file,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress: ({ loaded, total }) =>
          onProgress(Math.round((loaded * 100) / (total ?? file.size))),
      }
    );
    return data;
  } catch (e) {
    throw parseAxiosError(e, 'Unable to upload the offline bundle');
  }
}
